import React, { useEffect } from 'react';
import { Type, Minus, Plus } from 'lucide-react';
import { Slider } from './ui/slider';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';

// Phase 39.17 — Global text-size slider (accessibility).
// The chosen scale multiplies the CSS custom property --user-font-scale
// which we hook via index.css. Persisted in localStorage so the choice
// survives reload + auth changes.
const STORAGE_KEY = 'user-font-scale-v1';
const MIN_SCALE = 0.85;
const MAX_SCALE = 1.6;
const STEP = 0.05;

export const applyStoredFontScale = () => {
  const v = Number.parseFloat(localStorage.getItem(STORAGE_KEY) || '1') || 1;
  const scale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, v));
  document.documentElement.style.setProperty('--user-font-scale', String(scale));
};

const TextSizeSlider = () => {
  const [scale, setScale] = React.useState(() => {
    const v = Number.parseFloat(localStorage.getItem(STORAGE_KEY) || '1');
    return Number.isFinite(v) ? Math.max(MIN_SCALE, Math.min(MAX_SCALE, v)) : 1;
  });

  useEffect(() => {
    document.documentElement.style.setProperty('--user-font-scale', String(scale));
    localStorage.setItem(STORAGE_KEY, String(scale));
  }, [scale]);

  const bump = (delta) => setScale((s) => Math.max(MIN_SCALE, Math.min(MAX_SCALE, +(s + delta).toFixed(2))));

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="px-2" data-testid="text-size-slider-btn" title="Text size">
          <Type className="w-4 h-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64" data-testid="text-size-popover">
        <div className="text-sm font-medium mb-2">Text size</div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => bump(-STEP)} data-testid="text-size-decrease">
            <Minus className="w-3.5 h-3.5" />
          </Button>
          <Slider
            data-testid="text-size-slider"
            min={MIN_SCALE * 100}
            max={MAX_SCALE * 100}
            step={STEP * 100}
            value={[Math.round(scale * 100)]}
            onValueChange={(v) => setScale((v[0] || 100) / 100)}
            className="flex-1"
          />
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => bump(STEP)} data-testid="text-size-increase">
            <Plus className="w-3.5 h-3.5" />
          </Button>
        </div>
        <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
          <span>{Math.round(scale * 100)}%</span>
          <button
            onClick={() => setScale(1)}
            className="hover:text-foreground underline"
            data-testid="text-size-reset"
          >Reset</button>
        </div>
        <p className="mt-3 text-[11px] text-muted-foreground" style={{ fontSize: `${scale}em` }}>
          Preview: The quick brown fox.
        </p>
      </PopoverContent>
    </Popover>
  );
};

export default TextSizeSlider;
